import { useEffect, useState } from "react";
import { Save, Eye, Hammer, Lock, Download, Users, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";

export default function AdminConstructionTab() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newPwd, setNewPwd] = useState("");
    const [notifyList, setNotifyList] = useState([]);
    const [listLoading, setListLoading] = useState(true);

    useEffect(() => {
        reload();
    }, []);

    const reload = async () => {
        setLoading(true);
        setListLoading(true);
        try {
            const [s, list] = await Promise.all([
                api.get("/admin/construction/settings"),
                api.get("/admin/construction/notify-list"),
            ]);
            setSettings(s.data);
            setNotifyList(list.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
            setListLoading(false);
        }
    };

    const toggle = async (nextActive) => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/construction/settings", { active: nextActive });
            setSettings((s) => ({ ...s, active: data.active }));
            if (!nextActive && data.launch_emails_sent > 0) {
                toast.success(`Site ouvert publiquement. ${data.launch_emails_sent} email(s) de lancement envoyé(s) (mocked).`);
            } else {
                toast.success(nextActive ? "Mode construction activé." : "Site ouvert publiquement.");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const savePassword = async () => {
        if (!newPwd || newPwd.length < 4) {
            toast.error("Mot de passe : 4 caractères minimum.");
            return;
        }
        setSaving(true);
        try {
            await api.put("/admin/construction/settings", { password: newPwd });
            toast.success("Mot de passe mis à jour.");
            setNewPwd("");
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const exportCsv = () => {
        const token = localStorage.getItem("cursus_physio_token");
        const url = `${API}/admin/construction/notify-list.csv`;
        // Fetch with auth header and trigger download
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = "notify_on_launch.csv";
                document.body.appendChild(a);
                a.click();
                a.remove();
            })
            .catch(() => toast.error("Erreur export CSV"));
    };

    if (loading || !settings) {
        return <div className="py-12 text-center text-sm text-brand-smoke">Chargement…</div>;
    }

    return (
        <div className="space-y-6" data-testid="admin-construction-tab">
            {/* Toggle card */}
            <div className="rounded-2xl border border-brand-line bg-white p-8 shadow-premium">
                <div className="flex items-start justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-3">
                            <Hammer className="h-5 w-5 text-brand-orange" />
                            <h2 className="font-display text-2xl">Mode « Site en construction »</h2>
                        </div>
                        <p className="mt-2 max-w-xl text-sm text-brand-smoke">
                            Quand le mode est activé, toute URL publique (accueil, cursus, sessions…) affiche la page « Notre Institut prend forme ». Le login, l'admin, l'espace formateur et l'espace stagiaire restent accessibles normalement.
                        </p>
                        {settings.password_updated_at && (
                            <p className="mt-3 text-[11px] text-brand-smoke/80">
                                Mot de passe mis à jour le {new Date(settings.password_updated_at).toLocaleString("fr-FR")}.
                            </p>
                        )}
                    </div>
                    <div className="flex flex-shrink-0 flex-col items-end gap-2">
                        <button
                            type="button"
                            onClick={() => toggle(!settings.active)}
                            disabled={saving}
                            data-testid="construction-toggle"
                            className={`inline-flex h-9 w-20 items-center rounded-full transition-colors ${
                                settings.active ? "bg-brand-orange" : "bg-brand-line"
                            }`}
                            aria-pressed={settings.active}
                        >
                            <span
                                className={`inline-block h-7 w-7 rounded-full bg-white shadow transition-transform ${
                                    settings.active ? "translate-x-12" : "translate-x-1"
                                }`}
                            />
                        </button>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-ink">
                            {settings.active ? "Activé" : "Désactivé"}
                        </span>
                    </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                    <a
                        href="/construction"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="construction-preview-link"
                        className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 text-xs font-semibold text-brand-ink hover:border-brand-ink"
                    >
                        <Eye className="h-3.5 w-3.5" /> Prévisualiser la page
                        <ExternalLink className="h-3 w-3 opacity-60" />
                    </a>
                </div>
            </div>

            {/* Password card */}
            <div className="rounded-2xl border border-brand-line bg-white p-8 shadow-premium">
                <div className="flex items-center gap-3">
                    <Lock className="h-5 w-5 text-brand-orange" />
                    <h3 className="font-display text-xl">Mot de passe d'accès partenaire</h3>
                </div>
                <p className="mt-2 text-sm text-brand-smoke">
                    Les partenaires à qui vous communiquez ce mot de passe peuvent prévisualiser le site pendant 30 jours (cookie).
                </p>
                <div className="mt-5 flex flex-col gap-2.5 sm:flex-row sm:items-end">
                    <div className="flex-1">
                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Nouveau mot de passe</label>
                        <input
                            type="text"
                            value={newPwd}
                            onChange={(e) => setNewPwd(e.target.value)}
                            placeholder="Saisir un nouveau mot de passe"
                            data-testid="construction-new-password"
                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={savePassword}
                        disabled={saving || !newPwd}
                        data-testid="construction-save-password"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-coal disabled:opacity-60"
                    >
                        <Save className="h-3.5 w-3.5" /> {saving ? "…" : "Mettre à jour"}
                    </button>
                </div>
            </div>

            {/* Notify list */}
            <div className="rounded-2xl border border-brand-line bg-white p-8 shadow-premium">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Users className="h-5 w-5 text-brand-orange" />
                        <h3 className="font-display text-xl">Inscrits à la notification d'ouverture</h3>
                        <span className="rounded-full bg-brand-orange/10 px-2.5 py-0.5 text-[11px] font-semibold text-brand-orange">
                            {notifyList.length}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={exportCsv}
                        disabled={notifyList.length === 0}
                        data-testid="construction-export-csv"
                        className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 text-xs font-semibold text-brand-ink hover:border-brand-ink disabled:opacity-40"
                    >
                        <Download className="h-3.5 w-3.5" /> Exporter CSV
                    </button>
                </div>

                {listLoading ? (
                    <div className="py-8 text-center text-sm text-brand-smoke">Chargement…</div>
                ) : notifyList.length === 0 ? (
                    <div className="mt-6 rounded-xl border border-dashed border-brand-line bg-[#FAFAFA] px-6 py-12 text-center text-sm text-brand-smoke">
                        Aucun visiteur n'a encore laissé son email.
                    </div>
                ) : (
                    <div className="mt-6 overflow-x-auto">
                        <table className="w-full text-sm" data-testid="construction-notify-table">
                            <thead>
                                <tr className="border-b border-brand-line text-left text-[10px] uppercase tracking-wider text-brand-smoke">
                                    <th className="py-2 pr-4">Email</th>
                                    <th className="py-2 pr-4">Source</th>
                                    <th className="py-2 pr-4">IP</th>
                                    <th className="py-2 pr-4">Date</th>
                                    <th className="py-2 pr-4">Notifié</th>
                                </tr>
                            </thead>
                            <tbody>
                                {notifyList.map((n) => (
                                    <tr key={n.id || n.email} className="border-b border-brand-line/60">
                                        <td className="py-2 pr-4 text-brand-ink">{n.email}</td>
                                        <td className="py-2 pr-4 text-brand-smoke">{n.source || "—"}</td>
                                        <td className="py-2 pr-4 text-brand-smoke">{n.ip || "—"}</td>
                                        <td className="py-2 pr-4 text-brand-smoke">
                                            {n.created_at ? new Date(n.created_at).toLocaleString("fr-FR") : "—"}
                                        </td>
                                        <td className="py-2 pr-4 text-brand-smoke">
                                            {n.notified_at ? "✓" : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
